import React from "react";

import { Box, Button, IconButton, Paper, Popper, Portal, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { OnboardingButtonAction, OnboardingStep, PopperPlacement } from "../../../onboarding/types";

/**
 * Derives the Popper placement from the step config.
 * Falls back to the explicit popperPlacement field, then to anchorOrigin, then to a
 * sensible default based on whether the step is blocking or not.
 */
const getPopperPlacement = (step: OnboardingStep): PopperPlacement => {
    if (step.placement?.popperPlacement) return step.placement.popperPlacement;
    const anchor = step.placement?.anchorOrigin;
    if (!anchor) return step.nonBlocking ? "right-start" : "top-start";
    if (anchor.vertical === "top") {
        return anchor.horizontal === "right" ? "top-end" : "top-start";
    }
    if (anchor.vertical === "bottom") {
        if (anchor.horizontal === "center") return "bottom";
        return anchor.horizontal === "right" ? "bottom-end" : "bottom-start";
    }
    return "top-start";
};

/**
 * Returns the Popper offset [skidding, distance] in pixels.
 * Keeps the same semantics for right/left placements as the legacy nonBlocking path.
 * All values are treated as pixels (not MUI spacing units).
 */
const getPopperOffset = (step: OnboardingStep): [number, number] => {
    const placement = getPopperPlacement(step);
    const x = step.placement?.offset?.x ?? 0;
    const y = step.placement?.offset?.y ?? 0;
    if (placement.startsWith("right") || placement.startsWith("left")) {
        // For right/left: skidding along the side (vertical) = y, distance from edge = x
        return [y, Math.abs(x) || 12];
    }
    // For top/bottom/auto: skidding (horizontal shift) = x, distance (gap) = abs(y)
    return [x, Math.abs(y) || 8];
};

interface OnboardingBubbleProps {
    anchorEl: HTMLElement | null;
    isOpen: boolean;
    step: OnboardingStep;
    stepIndex: number;
    totalSteps: number;
    targetRect: DOMRect | null;
    hideContent?: boolean;
    onAction: (action: OnboardingButtonAction) => void;
    onDismiss: () => void;
}

const bubblePaperSx = {
    width: 360,
    maxWidth: "calc(100vw - 32px)",
    borderRadius: 3,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "#242424",
    bgcolor: "#242424",
    backgroundColor: "#242424",
    backgroundImage: "none",
    color: "#F6F7FB",
    boxShadow: "0 28px 70px rgba(0,0,0,0.45)",
    overflow: "hidden",
};

const overlayColor = "rgba(6, 10, 18, 0.52)";

const OnboardingBubble: React.FC<OnboardingBubbleProps> = ({ anchorEl, isOpen, step, stepIndex, totalSteps, targetRect, hideContent = false, onAction, onDismiss }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    if (!isOpen) return null;
    // When the anchor element fills most of the viewport (e.g. a full-screen modal on
    // tablet / small desktop) there is no edge with enough room for the Popper — use
    // the centered fallback instead so the bubble always stays fully visible.
    const targetFillsViewport =
        !isMobile &&
        targetRect != null &&
        (targetRect.width / window.innerWidth >= 0.6 || targetRect.height / window.innerHeight >= 0.8);
    const useCentered = isMobile || targetFillsViewport;
    const showOverlay = step.showOverlay !== false;
    const showHighlight = step.showHighlight !== false;
    const highlightPadding = step.highlightPadding ?? 0;
    const highlightTop = targetRect ? Math.max(targetRect.top - highlightPadding, 0) : 0;
    const highlightLeft = targetRect ? Math.max(targetRect.left - highlightPadding, 0) : 0;
    const highlightWidth = targetRect ? targetRect.width + highlightPadding * 2 : 0;
    const highlightHeight = targetRect ? targetRect.height + highlightPadding * 2 : 0;
    const highlightRight = highlightLeft + highlightWidth;
    const highlightBottom = highlightTop + highlightHeight;

    const content = (
        <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, p: 2.25, pb: 1 }}>
                <Box>
                    <Typography variant="overline" sx={{ display: "block", color: "rgba(255,255,255,0.6)", letterSpacing: "0.08em" }}>
                        Step {stepIndex + 1} of {totalSteps}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 0.5, color: "#FFFFFF" }}>
                        {step.title}
                    </Typography>
                </Box>
                <Tooltip title="Exit onboarding" PopperProps={{ sx: { zIndex: 1700 } }}>
                    <IconButton
                        size="small"
                        aria-label="Exit onboarding"
                        onClick={() => onDismiss()}
                        sx={{ color: "rgba(255,255,255,0.7)", mt: -0.5, mr: -0.5 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ px: 2.25, pb: 2 }}>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.6 }}>
                    {step.message}
                </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2.25, pt: 0 }}>
                {step.buttons.map(button => (
                    <Button
                        key={button.id}
                        variant={button.variant ?? "text"}
                        color={button.color ?? "inherit"}
                        onClick={() => onAction(button)}
                        sx={{ textTransform: "none", minWidth: 112 }}
                    >
                        {button.label}
                    </Button>
                ))}
            </Box>
        </>
    );

    return (
        <>
            {showOverlay || (showHighlight && targetRect) ? (
                <Portal>
                    {showOverlay ? (
                        targetRect ? (
                            <>
                                <Box
                                    sx={{
                                        position: "fixed",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: highlightTop,
                                        backgroundColor: overlayColor,
                                        zIndex: 1500,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "fixed",
                                        top: highlightTop,
                                        left: 0,
                                        width: highlightLeft,
                                        height: highlightHeight,
                                        backgroundColor: overlayColor,
                                        zIndex: 1500,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "fixed",
                                        top: highlightTop,
                                        left: highlightRight,
                                        right: 0,
                                        height: highlightHeight,
                                        backgroundColor: overlayColor,
                                        zIndex: 1500,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "fixed",
                                        top: highlightBottom,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: overlayColor,
                                        zIndex: 1500,
                                    }}
                                />
                            </>
                        ) : (
                            <Box
                                sx={{
                                    position: "fixed",
                                    inset: 0,
                                    backgroundColor: overlayColor,
                                    zIndex: 1500,
                                }}
                            />
                        )
                    ) : null}
                    {showHighlight && targetRect ? (
                        <Box
                            sx={{
                                position: "fixed",
                                top: highlightTop,
                                left: highlightLeft,
                                width: highlightWidth,
                                height: highlightHeight,
                                borderRadius: 2,
                                boxShadow: "0 0 0 2px rgba(255,255,255,0.92)",
                                zIndex: 1501,
                                pointerEvents: "none",
                            }}
                        />
                    ) : null}
                </Portal>
            ) : null}
            {!hideContent && step.target && !useCentered ? (
                <Popper
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    placement={getPopperPlacement(step)}
                    popperOptions={{ strategy: "fixed" }}
                    sx={{ zIndex: 1600 }}
                    modifiers={[
                        { name: "offset", options: { offset: getPopperOffset(step) } },
                        { name: "flip", options: { padding: 16, fallbackPlacements: ["bottom-start", "top-start", "left-start", "bottom", "top"] } },
                        { name: "preventOverflow", options: { padding: 16 } },
                    ]}
                >
                    <Paper elevation={0} sx={bubblePaperSx}>
                        {content}
                    </Paper>
                </Popper>
            ) : !hideContent ? (
                <Portal>
                    <Box
                        sx={{
                            position: "fixed",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 2,
                            zIndex: 1600,
                        }}
                    >
                        <Paper elevation={0} sx={bubblePaperSx}>
                            {content}
                        </Paper>
                    </Box>
                </Portal>
            ) : null}
        </>
    );
};

export default OnboardingBubble;
